// Phase C2 — React Query hooks for /wb/cameras.
//
// Server module: server/src/modules/wb/cameras. Endpoints return the
// usual { success, data } envelope; list keyed by an optional
// weighbridge_id / yard_id filter, detail/upsert/delete by enc_id.
//
// connection_string_encrypted is plaintext on the wire (the server
// encrypts at rest) and is NEVER returned in any response.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Types ------------------------------------------------------------------

export type WbCameraType = 'overview' | 'anpr' | 'material' | 'plate';
export type WbCameraProtocol = 'rtsp' | 'onvif' | 'http_mjpeg' | 'usb';

export interface WbCamera {
  enc_id: string;
  yard: { enc_id: string; name: string };
  weighbridge: { enc_id: string; name: string } | null;
  name: string;
  type: WbCameraType;
  protocol: WbCameraProtocol;
  anpr_enabled: boolean;
  anpr_min_confidence: number;
  snapshot_path_template: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCameraBody {
  yard_id: string;
  weighbridge_id?: string | null;
  name: string;
  type: WbCameraType;
  protocol: WbCameraProtocol;
  connection_string_encrypted?: string;
  anpr_enabled?: boolean;
  anpr_min_confidence?: number;
  snapshot_path_template?: string;
  is_active?: boolean;
}

export interface UpdateCameraBody {
  weighbridge_id?: string | null;
  name?: string;
  type?: WbCameraType;
  protocol?: WbCameraProtocol;
  connection_string_encrypted?: string | null;
  anpr_enabled?: boolean;
  anpr_min_confidence?: number;
  snapshot_path_template?: string | null;
  is_active?: boolean;
}

// -- List -------------------------------------------------------------------

export interface UseWbCamerasOpts {
  weighbridgeEncId?: string;
  yardEncId?: string;
}

export function useWbCameras(opts: UseWbCamerasOpts = {}) {
  const filters: Record<string, string> = {};
  if (opts.weighbridgeEncId) filters['weighbridge_id'] = opts.weighbridgeEncId;
  if (opts.yardEncId) filters['yard_id'] = opts.yardEncId;
  const qs = new URLSearchParams(filters).toString();
  const url = qs ? `${endpoints.wb.cameras}?${qs}` : endpoints.wb.cameras;
  return useQuery({
    queryKey: queryKeys.wb.cameras(filters),
    queryFn: async () => {
      const res = await apiClient.get<{ cameras: WbCamera[] }>(url);
      return res.data;
    },
  });
}

// -- Detail -----------------------------------------------------------------

export function useWbCamera(encId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.wb.camera(encId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<{ camera: WbCamera }>(
        endpoints.wb.camera(encId as string),
      );
      return res.data;
    },
    enabled: Boolean(encId) && encId !== 'new',
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCameraBody) => {
      const res = await apiClient.post<{ camera: WbCamera }, CreateCameraBody>(
        endpoints.wb.cameras,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'cameras'] });
    },
  });
}

export function useUpdateCamera(encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateCameraBody) => {
      const res = await apiClient.patch<{ camera: WbCamera }, UpdateCameraBody>(
        endpoints.wb.camera(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'cameras'] });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.camera(encId) });
    },
  });
}

export function useDeleteCamera(encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.camera(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'cameras'] });
    },
  });
}
